import { useState, useMemo, useEffect } from 'react';

import { motion, AnimatePresence } from 'motion/react';
import { Search } from 'lucide-react';
import {
  ClockIcon, WeatherIcon, RSSFeedIcon, GoogleSheetIcon, GoogleSlidesIcon,
  TextIcon, SocialWallIcon, ScrollerIcon, FacebookIcon, TOINewsIcon,
  ESPNNewsIcon, CNNNewsIcon, CNNLatinoIcon, NDTVIcon, FoxNewsIcon,
  InstagramIcon, BBCNewsIcon
} from './AppIcons';
import TextApp from './TextApp';

// App Data Definition
const APPS = [
  { id: 'Clock', name: 'Clock', desc: 'Stay in sync with global time—your stylish world clock, tailored for every screen.', icon: <ClockIcon />, category: 'Clock' },
  { id: 'Weather', name: 'Weather', desc: 'Show live weather conditions instantly, tailored to your screen\'s location.', icon: <WeatherIcon />, category: 'Weather' },
  { id: 'RSS Feed', name: 'RSS Feed', desc: 'Stream live headlines to your signage with a simple RSS URL and customizable themes.', icon: <RSSFeedIcon />, category: 'RSS Feed' },
  { id: 'Google Sheet', name: 'Google Sheet', desc: 'Convert your Google Sheet into an engaging gallery of profiles.', icon: <GoogleSheetIcon />, category: 'Business' },
  { id: 'Google Slides', name: 'Google Slides', desc: 'Turn your spreadsheet into an interactive showcase of profiles.', icon: <GoogleSlidesIcon />, category: 'Business' },
  { id: 'Text', name: 'Text', desc: 'Display quick, customisable messages on screens—perfect for signs, alerts, or updates.', icon: <TextIcon />, category: 'Business' },
  { id: 'Social Wall', name: 'Social Wall', desc: 'Display live social feeds with branded themes and full moderation control.', icon: <SocialWallIcon />, category: 'Social Media' },
  { id: 'Scroller', name: 'Scroller', desc: 'Turn any screen area into a dynamic ticker with custom messages, colors, and speed.', icon: <ScrollerIcon />, category: 'News' },
  { id: 'Facebook', name: 'Facebook', desc: 'Display live Facebook posts on any screen with automatic updates and clean presentation.', icon: <FacebookIcon />, category: 'Social Media' },
  { id: 'TOI News', name: 'TOI News', desc: 'Provides timely editorial content from TOI directly to digital displays for continuous audience engagement.', icon: <TOINewsIcon />, category: 'News' },
  { id: 'ESPN News', name: 'ESPN News', desc: 'Provides timely editorial content from ESPN directly to digital displays for continuous audience engagement.', icon: <ESPNNewsIcon />, category: 'News' },
  { id: 'CNN News', name: 'CNN News', desc: 'Provides timely editorial content from CNN directly to digital displays for continuous audience engagement.', icon: <CNNNewsIcon />, category: 'News' },
  { id: 'CNN Latino News', name: 'CNN Latino News', desc: 'Provides timely editorial content from TOI directly to digital displays for continuous audience engagement.', icon: <CNNLatinoIcon />, category: 'News' },
  { id: 'NDTV News', name: 'NDTV News', desc: 'Turn any screen area into a dynamic ticker with custom messages, colors, and speed.', icon: <NDTVIcon />, category: 'News' },
  { id: 'Fox News', name: 'Fox News', desc: 'Stream breaking news and live updates from Fox News directly to your screens with automatic refresh.', icon: <FoxNewsIcon />, category: 'News' },
  { id: 'Instagram', name: 'Instagram', desc: 'Display live Instagram posts with auto-updates, captions, and theme controls on any screen.', icon: <InstagramIcon />, category: 'Social Media' },
  { id: 'BBC News', name: 'BBC News', desc: 'Stream trusted BBC news in real time—perfect for offices and public screens.', icon: <BBCNewsIcon />, category: 'News' },
];

const CATEGORIES = [
  'All Apps', 'Weather', 'Clock', 'RSS Feed', 'News', 'Social Media', 'Healthcare', 'Restaurant', 'Business'
];

interface SelectAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAppCreated?: (appData: any) => void;
}

export default function SelectAppModal({ isOpen, onClose, onAppCreated }: SelectAppModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Apps');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

  // Reset selected app when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset slightly to allow exit animation to complete if needed, 
      // but instant reset is usually safer to avoid flashing list on close.
      // However, if we reset instantly, the exit animation will show the list view.
      // So let's reset it after the exit animation duration (e.g., 300ms).
      const timer = setTimeout(() => setSelectedAppId(null), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const filteredApps = useMemo(() => {
    return APPS.filter(app => {
      const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase());


      if (selectedCategory === 'All Apps') return matchesSearch;
      return matchesSearch && app.category === selectedCategory;
    });
  }, [searchQuery, selectedCategory]);

  const handleAppClick = (appId: string) => {
    if (appId === 'Text') {
      setSelectedAppId(appId);
    }
  };

  const handleCreate = (data: any) => {
    if (onAppCreated) {
      onAppCreated(data);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          {/* Modal Container */}
          <motion.div
            key="modal-container"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="bg-white relative rounded-[8px] w-full max-w-[1200px] h-fit max-h-[900px] overflow-hidden flex flex-col shadow-2xl"
          >
            <AnimatePresence mode="wait">
              {selectedAppId === 'Text' ? (
                <motion.div
                  key="text-app"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="h-fit w-full"
                >
                  <TextApp onBack={() => setSelectedAppId(null)} onCreate={handleCreate} />
                </motion.div>
              ) : (
                <motion.div
                  key="app-list"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="flex flex-col h-[80vh] overflow-hidden"
                >
                  {/* Header (Frame) */}
                  <div className="flex items-center justify-between p-[24px] pb-0 shrink-0">
                    <p className="font-['Inter'] font-bold text-[#101928] text-[20px]">Select App</p>
                    <button
                      onClick={onClose}
                      className="cursor-pointer rounded-[6px] size-[36px] flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 7L7 17M7 7L17 17" stroke="#667185" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  {/* Content (Frame 70) */}
                  <div className="flex flex-col gap-[24px] p-[24px] overflow-hidden h-full">

                    {/* Search Bar */}
                    <div className="h-[36px] relative rounded-[6px] shrink-0 w-[362px]">
                      <div className="absolute inset-0 border border-[#e4e7ec] rounded-[6px] pointer-events-none" />
                      <div className="flex items-center px-[12px] py-[3px] size-full gap-[8px]">
                        <Search className="w-[16px] h-[16px] text-[#98A2B3]" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="I’m looking for..."
                          className="w-full h-full outline-none text-[14px] text-[#101928] placeholder-[#98a2b3] font-['Inter']"
                        />
                      </div>
                    </div>

                    {/* Categories (Frame 69) */}
                    <div className="flex flex-wrap gap-[12px] items-center shrink-0">
                      {CATEGORIES.map(category => (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`
                               flex items-center justify-center px-[12px] h-[34px] rounded-[6px] border text-[13px] font-medium transition-colors text-center
                               ${selectedCategory === category
                              ? 'bg-[#E7F6EC] border-[#0ba29b] text-[#227735]'
                              : 'bg-white border-[#e4e7ec] text-[#344054] hover:bg-gray-50'
                            }
                             `}
                        >
                          {category}
                        </button>
                      ))}
                    </div>

                    {/* Apps Grid (Frame 165) */}
                    <div className="flex flex-wrap content-start gap-x-[16px] gap-y-[28px] w-full pb-10 flex-1 overflow-y-auto min-h-0 pr-2">
                      {filteredApps.map(app => (
                        <div
                          key={app.id}
                          onClick={() => handleAppClick(app.id)}
                          className="relative w-[362px] h-[122px] shrink-0 group cursor-pointer"
                        >
                          {/* Background Card */}
                          <div className="absolute top-[21px] left-0 w-[362px] h-[101px] bg-white border border-[#f0f2f5] rounded-[8px] overflow-hidden transition-shadow group-hover:shadow-md">
                            {/* Text Content */}
                            <div className="absolute top-[30px] left-[17px] w-[324px] flex flex-col gap-[14px]">
                              <p className="font-['Inter'] font-semibold text-[#101928] text-[14px] leading-none">
                                {app.name}
                              </p>
                              <p className="font-['Inter'] font-normal text-[#344054] text-[12px] leading-[20px]">
                                {app.desc}
                              </p>
                            </div>
                          </div>

                          {/* Icon (Overlaps top border) */}
                          <div className="relative z-10">
                            {app.icon}
                          </div>
                        </div>
                      ))}
                      {filteredApps.length === 0 && (
                        <div className="w-full text-center py-10 text-gray-500">No apps found.</div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}